import React, { type ReactNode } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import CommentIcon from '@atlaskit/icon/core/comment';
import LockLoadedIcon from '@atlaskit/icon/core/lock-locked';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FeatureRow } from './feature-row';
import { messages } from './messages';

const featureNameColumnStyles = cssMap({
	root: {
		width: '210px',
		height: '312px',
		backgroundColor: token('color.background.input'),
		paddingTop: token('space.800'),
	},
});

/**
 * Returns the 6 features we show based on the product. Right now, only Confluence and Jira are supported.
 *
 * @param isConfluence Confluence if true, Jira otherwise.
 * @param formatMessage A function that can provide translations.
 */
function getFeatureRows(
	isConfluence: boolean,
	formatMessage: (descriptor: MessageDescriptor) => string,
): ReactNode[] {
	const rows: ReactNode[] = [];

	rows.push(
		<FeatureRow
			key="unlimitedUsers"
			name={formatMessage(messages.unlimitedUsers)}
			icon={<PersonAddIcon label="" color="currentColor" />}
			keyName="unlimitedUsers"
		/>,
	);

	rows.push(
		<FeatureRow
			key="permissions"
			name={
				isConfluence
					? formatMessage(messages.pagePermissions)
					: formatMessage(messages.userPermissions)
			}
			icon={<LockLoadedIcon label="" color="currentColor" />}
			keyName="permissions"
		/>,
	);

	if (isConfluence) {
		rows.push(
			<FeatureRow
				key="confluenceFreeGuestAccess"
				name={formatMessage(messages.freeGuestAccess)}
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				keyName="confluenceFreeGuestAccess"
			/>,
		);

		rows.push(
			<FeatureRow
				key="pageInsights"
				name={formatMessage(messages.pageInsights)}
				icon={<ChartTrendUpIcon label="" color="currentColor" />}
				keyName="pageInsights"
			/>,
		);
	} else {
		rows.push(
			<FeatureRow
				key="atlassianSupport"
				name={formatMessage(messages.atlassianSupport)}
				icon={<CommentIcon label="" color="currentColor" />}
				keyName="atlassianSupport"
			/>,
		);

		rows.push(
			<FeatureRow
				key="jiraFreeGuestAccess"
				name={formatMessage(messages.freeGuestAccess)}
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				keyName="jiraFreeGuestAccess"
			/>,
		);
	}

	rows.push(
		<FeatureRow
			key="rovo"
			name={formatMessage(messages.rovo)}
			icon={<RovoIcon label="" color="currentColor" />}
			keyName="rovo"
		/>,
	);

	rows.push(
		<FeatureRow
			key="whiteboards"
			name={formatMessage(messages.whiteboards)}
			icon={<WhiteboardIcon label="" color="currentColor" />}
			keyName="whiteboards"
		/>,
	);

	return rows;
}

/**
 * Shows a stack of features.
 */
export const FeatureNameColumn = ({
	isConfluence,
}: {
	isConfluence: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={featureNameColumnStyles.root} justifyContent="center">
			<Flex direction="column" gap="space.200">
				{getFeatureRows(isConfluence, formatMessage)}
			</Flex>
		</Flex>
	);
};
